import { useState } from "react";
import {
  Gift,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Star,
  StarOff,
  Search,
  Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useGiftCards, GiftCard } from "@/hooks/useGiftCards";
import { format } from "date-fns";

const GiftCardsManagementSection = () => {
  const {
    giftCards,
    purchases,
    loading,
    createGiftCard,
    updateGiftCard,
    deleteGiftCard,
  } = useGiftCards();

  const [showCardDialog, setShowCardDialog] = useState(false);
  const [editingCard, setEditingCard] = useState<GiftCard | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [purchaseSearchTerm, setPurchaseSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    name_en: "",
    name_ru: "",
    description: "",
    description_en: "",
    description_ru: "",
    amount: 0,
    discount_percentage: 0,
    validity_days: 365,
    image_url: "",
    background_color: "#10b981",
    is_active: true,
    is_featured: false,
    order_index: 0,
  });

  const resetForm = () => {
    setFormData({
      name: "",
      name_en: "",
      name_ru: "",
      description: "",
      description_en: "",
      description_ru: "",
      amount: 0,
      discount_percentage: 0,
      validity_days: 365,
      image_url: "",
      background_color: "#10b981",
      is_active: true,
      is_featured: false,
      order_index: 0,
    });
    setEditingCard(null);
  };

  const handleOpenDialog = (card?: GiftCard) => {
    if (card) {
      setEditingCard(card);
      setFormData({
        name: card.name,
        name_en: card.name_en || "",
        name_ru: card.name_ru || "",
        description: card.description || "",
        description_en: card.description_en || "",
        description_ru: card.description_ru || "",
        amount: card.amount,
        discount_percentage: card.discount_percentage || 0,
        validity_days: card.validity_days || 365,
        image_url: card.image_url || "",
        background_color: card.background_color || "#10b981",
        is_active: card.is_active ?? true,
        is_featured: card.is_featured ?? false,
        order_index: card.order_index || 0,
      });
    } else {
      resetForm();
    }
    setShowCardDialog(true);
  };

  const handleSubmit = async () => {
    if (editingCard) {
      await updateGiftCard(editingCard.id, formData);
    } else {
      await createGiftCard(formData);
    }
    setShowCardDialog(false);
    resetForm();
  };

  const handleDelete = async (id: string) => {
    if (confirm("Վստա՞հ եք ցանկանում ջնջել նվեր քարտը։")) {
      await deleteGiftCard(id);
    }
  };

  const handleToggleActive = async (card: GiftCard) => {
    await updateGiftCard(card.id, { is_active: !card.is_active });
  };

  const handleToggleFeatured = async (card: GiftCard) => {
    await updateGiftCard(card.id, { is_featured: !card.is_featured });
  };

  const filteredCards = giftCards.filter(
    (card) =>
      card.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      card.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPurchases = purchases.filter(
    (purchase) =>
      purchase.buyer_name.toLowerCase().includes(purchaseSearchTerm.toLowerCase()) ||
      purchase.buyer_email.toLowerCase().includes(purchaseSearchTerm.toLowerCase()) ||
      purchase.recipient_name.toLowerCase().includes(purchaseSearchTerm.toLowerCase()) ||
      purchase.gift_code.toLowerCase().includes(purchaseSearchTerm.toLowerCase())
  );

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-500">Վճարված</Badge>;
      case "pending":
        return <Badge variant="secondary">Սպասվում է</Badge>;
      case "failed":
        return <Badge variant="destructive">Չհաջողվեց</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const totalRevenue = purchases
    .filter((p) => p.payment_status === "completed")
    .reduce((sum, p) => sum + p.amount, 0);

  const redeemedCards = purchases.filter((p) => p.is_redeemed).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Բեռնվում է...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-border/70 bg-card/80">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Նվեր քարտեր</p>
                <p className="text-2xl font-bold text-muted-foreground">{giftCards.length}</p>
              </div>
              <Gift className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/70 bg-card/80">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Գնումներ</p>
                <p className="text-2xl font-bold text-muted-foreground">{purchases.length}</p>
              </div>
              <Gift className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/70 bg-card/80">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Ընդհանուր եկամուտ</p>
                <p className="text-2xl font-bold text-muted-foreground">
                  {totalRevenue.toLocaleString()} ֏
                </p>
              </div>
              <Gift className="h-8 w-8 text-yellow-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/70 bg-card/80">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Օգտագործված</p>
                <p className="text-2xl font-bold text-muted-foreground">{redeemedCards}</p>
              </div>
              <Gift className="h-8 w-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="cards" className="w-full">
        <TabsList className="grid w-full grid-cols-2 bg-muted border border-border/70">
          <TabsTrigger
            value="cards"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            Քարտեր
          </TabsTrigger>
          <TabsTrigger
            value="purchases"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            Գնումներ ({purchases.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cards" className="mt-6">
          <Card className="border-border/70 bg-card/80">
            <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="text-muted-foreground flex items-center gap-2">
                <Gift className="h-5 w-5" />
                Նվեր քարտերի կառավարում
              </CardTitle>
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Փնտրել..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-full bg-muted border-border/70 text-muted-foreground sm:w-64"
                  />
                </div>
                <Button
                  onClick={() => handleOpenDialog()}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ավելացնել
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border/70">
                    <TableHead className="text-muted-foreground">Անուն</TableHead>
                    <TableHead className="text-muted-foreground">Գումար</TableHead>
                    <TableHead className="text-muted-foreground">Վավերություն</TableHead>
                    <TableHead className="text-muted-foreground">Ակտիվ</TableHead>
                    <TableHead className="text-muted-foreground">Առաջարկվող</TableHead>
                    <TableHead className="text-muted-foreground">Գործողություններ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCards.map((card) => (
                    <TableRow key={card.id} className="border-border/70">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-6 rounded"
                            style={{
                              backgroundColor: card.background_color || "#10b981",
                            }}
                          />
                          <span className="text-muted-foreground font-medium">{card.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {card.amount.toLocaleString()} ֏
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {card.validity_days} օր
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(card)}
                          className={card.is_active ? "text-primary" : "text-muted-foreground"}
                        >
                          {card.is_active ? (
                            <Eye className="h-4 w-4" />
                          ) : (
                            <EyeOff className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleFeatured(card)}
                          className={card.is_featured ? "text-yellow-400" : "text-muted-foreground"}
                        >
                          {card.is_featured ? (
                            <Star className="h-4 w-4 fill-yellow-400" />
                          ) : (
                            <StarOff className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(card)}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(card.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="purchases" className="mt-6">
          <Card className="border-border/70 bg-card/80">
            <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="text-muted-foreground flex items-center gap-2">
                <Gift className="h-5 w-5" />
                Գնումների ցուցակ
              </CardTitle>
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Փնտրել գնումներ..."
                    value={purchaseSearchTerm}
                    onChange={(e) => setPurchaseSearchTerm(e.target.value)}
                    className="pl-10 w-full bg-muted border-border/70 text-muted-foreground sm:w-64"
                  />
                </div>
                <Button variant="outline" className="border-border/70 text-muted-foreground">
                  <Download className="h-4 w-4 mr-2" />
                  Արտահանել
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border/70">
                    <TableHead className="text-muted-foreground">Կոդ</TableHead>
                    <TableHead className="text-muted-foreground">Գնորդ</TableHead>
                    <TableHead className="text-muted-foreground">Ստացող</TableHead>
                    <TableHead className="text-muted-foreground">Գումար</TableHead>
                    <TableHead className="text-muted-foreground">Կարգավիճակ</TableHead>
                    <TableHead className="text-muted-foreground">Օգտագործված</TableHead>
                    <TableHead className="text-muted-foreground">Ամսաթիվ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPurchases.map((purchase) => (
                    <TableRow key={purchase.id} className="border-border/70">
                      <TableCell className="text-muted-foreground">
                        {purchase.gift_code}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {purchase.buyer_name}
                        <div className="text-xs text-muted-foreground">{purchase.buyer_email}</div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {purchase.recipient_name}
                        <div className="text-xs text-muted-foreground">{purchase.recipient_email}</div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {purchase.amount.toLocaleString()} ֏
                      </TableCell>
                      <TableCell>{getStatusBadge(purchase.payment_status)}</TableCell>
                      <TableCell>
                        {purchase.is_redeemed ? (
                          <Badge className="bg-purple-500">Օգտագործված</Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="border-border/70 text-muted-foreground"
                          >
                            Չօգտագործված
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {purchase.created_at &&
                          format(new Date(purchase.created_at), "dd/MM/yyyy")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Gift Card Dialog */}
      <Dialog open={showCardDialog} onOpenChange={setShowCardDialog}>
        <DialogContent className="max-w-2xl border-border/70 bg-card/80">
            <DialogHeader>
            <DialogTitle className="text-muted-foreground">
              {editingCard ? "Խմբագրել նվեր քարտը" : "Ստեղծել նվեր քարտ"}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Լրացրեք նվեր քարտի տվյալները։
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Անուն (Հայերեն) *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-muted border-border/70 text-muted-foreground"
                placeholder="Մուտքագրեք անունը..."
              />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Անուն (Eng)</Label>
              <Input
                value={formData.name_en}
                onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                className="bg-muted border-border/70 text-muted-foreground"
                placeholder="Name in English..."
              />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Անուն (Rus)</Label>
              <Input
                value={formData.name_ru}
                onChange={(e) => setFormData({ ...formData, name_ru: e.target.value })}
                className="bg-muted border-border/70 text-muted-foreground"
                placeholder="Մուտքագրեք անունը..."
              />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Գումար *</Label>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) =>
                  setFormData({ ...formData, amount: parseInt(e.target.value) || 0 })
                }
                className="bg-muted border-border/70 text-muted-foreground"
                placeholder="50000"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Զեղչ (%)</Label>
              <Input
                type="number"
                value={formData.discount_percentage}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    discount_percentage: parseInt(e.target.value) || 0,
                  })
                }
                className="bg-muted border-border/70 text-muted-foreground"
                placeholder="10"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Վավերության օրեր (օր)</Label>
              <Input
                type="number"
                value={formData.validity_days}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    validity_days: parseInt(e.target.value) || 365,
                  })
                }
                className="bg-muted border-border/70 text-muted-foreground"
                placeholder="365"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Ֆոնի գույն</Label>
              <Input
                type="color"
                value={formData.background_color}
                onChange={(e) =>
                  setFormData({ ...formData, background_color: e.target.value })
                }
                className="bg-muted border-border/70 h-10 w-full"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Դասավորության համար</Label>
              <Input
                type="number"
                value={formData.order_index}
                onChange={(e) =>
                  setFormData({ ...formData, order_index: parseInt(e.target.value) || 0 })
                }
                className="bg-muted border-border/70 text-muted-foreground"
                placeholder="0"
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label className="text-muted-foreground">Նկարագրություն (Հայերեն)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="bg-muted border-border/70 text-muted-foreground"
                placeholder="Մուտքագրեք նկարագրությունը..."
                rows={3}
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label className="text-muted-foreground">Նկարի URL</Label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="bg-muted border-border/70 text-muted-foreground"
                placeholder="https://..."
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_active: checked })
                }
              />
              <Label className="text-muted-foreground">Ակտիվ</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.is_featured}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_featured: checked })
                }
              />
              <Label className="text-muted-foreground">Առաջարկվող</Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCardDialog(false)}
              className="border-border/70 text-muted-foreground"
            >
              Չեղարկել
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {editingCard ? "Պահպանել" : "Ստեղծել"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GiftCardsManagementSection;
